import { useFormik } from 'formik'
import { TFuncKey, useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { DisbursementType } from '@percent/admin-dashboard/api/actions/disbursements/disbursements.types'
import { UseRemoveFromBatchFormProps } from './useRemoveFromBatchForm.types'

export const reasonsByType: Record<string, { code: string; label: TFuncKey }[]> = {
  [DisbursementType.INTERNAL]: [
    { code: 'BANK_DETAILS_INVALID', label: 'disbursementCancel.reason.bankDetailsInvalid' },
    { code: 'COMPLIANCE_REJECTION', label: 'disbursementCancel.reason.complianceRejection' },
    { code: 'Other', label: 'disbursementCancel.reason.other' }
  ],
  [DisbursementType.EXTERNAL]: [
    { code: 'CANNOT_PAY_BY_CARD', label: 'disbursementCancel.reason.cannotPayByCard' },
    { code: 'COMPLIANCE_REJECTION', label: 'disbursementCancel.reason.complianceRejection' },
    { code: 'Other', label: 'disbursementCancel.reason.other' }
  ],
  [DisbursementType.CHEQUE]: [
    { code: 'CANNOT_PAY_BY_CHEQUE', label: 'disbursementCancel.reason.cannotPayByCheque' },
    { code: 'COMPLIANCE_REJECTION', label: 'disbursementCancel.reason.complianceRejection' },
    { code: 'Other', label: 'disbursementCancel.reason.other' }
  ]
}

function getReasonsForTypes(types: string[]) {
  const allReasons = types.flatMap(type => reasonsByType[type] ?? [])

  const unique = allReasons.filter((reason, index) => allReasons.findIndex(r => r.code === reason.code) === index)

  return unique.sort((a, b) => {
    if (a.code === 'Other') {
      return 1
    }

    if (b.code === 'Other') {
      return -1
    }

    return 0
  })
}

export function useRemoveFromBatchForm({ onFormSubmit, disbursementTypes }: UseRemoveFromBatchFormProps) {
  const { t } = useTranslation()

  const reasons =
    disbursementTypes.length > 0 ? getReasonsForTypes(disbursementTypes) : reasonsByType[DisbursementType.INTERNAL]

  const formik = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      cancelReason: '',
      customReason: ''
    },
    validationSchema: () =>
      Yup.object().shape({
        cancelReason: Yup.string()
          .oneOf(reasons.map(r => r.code))
          .required(t('errorMessage.required')),
        customReason: Yup.string()
          .nullable()
          .optional()
          .when('cancelReason', {
            is: 'Other',
            then: Yup.string()
              .required(t('errorMessage.required'))
              .trim()
              .min(1, t('errorMessage.required'))
              .max(255, t('errorMessage.orgFieldCharacterLimit', { fieldName: 'Custom reason' }))
          })
      }),
    onSubmit: async ({ cancelReason, customReason }) => {
      const reason = cancelReason === 'Other' ? customReason : cancelReason

      await onFormSubmit({ reason })
    }
  })

  return {
    formik,
    reasonOptions: reasons.map(reason => ({
      label: t(reason.label),
      value: reason.code
    }))
  }
}
